<template>
  <button
    v-tooltip="title"
    :class="buttonClasses"
    :disabled="isDisabled"
    :shortkey-target="effectiveShortkeyTarget"
    @click="$emit('click', $event)"
    @mousedown="$emit('mousedown', $event)"
    @keydown="$emit('keydown', $event)"
    @focus="$emit('focus', $event)"
  >
    <!-- Left Icon -->
    <div
      v-if="showLeftIcon"
      :class="leftIconClasses"
    >
      &nbsp;
    </div>

    <!-- Text Content -->
    <div
      v-if="showText"
      :class="textClasses"
    >
      {{ text }}
    </div>

    <!-- Right Icon -->
    <div
      v-if="showRightIcon"
      :class="rightIconClasses"
    />

    <!-- Slot Content -->
    <div
      v-if="$slots.default"
      :class="slotClasses"
    >
      <slot />
    </div>
  </button>
</template>

<script>
export default {
  name: 'MsButton'
}
</script>

<script setup>
import { useButton } from './composables/useButton.js'
import { useButtonEvents } from './composables/useButtonEvents.js'
import { useButtonStyles } from './composables/useButtonStyles.js'
import { VALID_BUTTON_TYPES, DEFAULT_PROPS, BUTTON_EVENTS } from './constants/button-constants.js'

/**
 * MsButton Component - Refactored từ code gốc giữ nguyên logic
 */

/**
 * Component props definition - giữ nguyên các props gốc
 */
const props = defineProps({
  isLagerIcon: {
    type: Boolean,
    default: DEFAULT_PROPS.isLagerIcon
  },
  title: {
    type: String,
    default: null
  },
  text: {
    type: String,
    default: null
  },
  leftIcon: {
    type: String,
    default: null
  },
  rightIcon: {
    type: String,
    default: null
  },
  type: {
    type: String,
    default: DEFAULT_PROPS.type,
    validator: (value) => VALID_BUTTON_TYPES.includes(value)
  },
  disabled: {
    type: Boolean,
    default: DEFAULT_PROPS.disabled
  },
  processing: {
    type: Boolean,
    default: DEFAULT_PROPS.processing
  },
  radius: {
    type: Boolean,
    default: DEFAULT_PROPS.radius
  },
  hasBorder: {
    type: Boolean,
    default: DEFAULT_PROPS.hasBorder
  },
  shortkeyTarget: {
    type: String,
    default: DEFAULT_PROPS.shortkeyTarget
  },
})

/**
 * Component events definition
 */
const emit = defineEmits(BUTTON_EVENTS)

/**
 * Use composables for modular functionality
 */

// Core button logic
const buttonState = useButton(props)
const {
  isDisabled,
  isProcessing,
  hasText,
  hasLeftIcon,
  hasRightIcon,
  isIconOnly,
  iconSizeClass,
  effectiveType,
  isValidType,
  effectiveShortkeyTarget
} = buttonState

// Event handling - giữ nguyên logic gốc (tạo listeners nhưng không bind)
const {
  listeners
} = useButtonEvents(props, emit)

// Styling logic - sử dụng computed variables thay vì hard-code
const {
  buttonClasses,
  leftIconClasses,
  rightIconClasses,
  textClasses,
  slotClasses,
  showLeftIcon,
  showRightIcon,
  showText
} = useButtonStyles(props, buttonState)

// Expose component API - chỉ expose những gì cần thiết cho testing
defineExpose({
  // State
  isDisabled,
  isProcessing,
  
  // Content
  hasText,
  hasLeftIcon,
  hasRightIcon,
  isIconOnly,
  
  // Type validation
  isValidType,
  effectiveType,
  
  // Giữ listeners để tương thích với code gốc
  listeners
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/components/MsButton.scss';

.margin-left-4 {
  margin-left: 4px;
}
</style>
